/**
 * IMPORTANT: Requires that you closely follow the naming convention proscribed.
 * Injects CSS into JS with appropriate media queries for loading bg images.
 * @param {Object} obj // an object with the following properties
 * @param {String} obj.className // css class to which bg image will be applied
 * @param {String} obj.backgroundImageName // E.g. [IMAGE_NAME]_desktop
 * @param {String} obj.directory // Must be in public/static-preauth/images
 * @param {Array} obj.filetypeArr // Array of 3 for [mobileFileType, tabletFiletype, desktopFileType]
 * @param {Boolean} obj.notVersioned // Bool indicating the assets do not have (@2x) versions.
 */

import { DESKTOP_MIN, TABLET_MIN } from '~/libs/universal/utils/device';

type renderAsBackgroundImageInterface = {
  selector: string;
  backgroundImageName: string;
  directory: string;
  filetype: [string, string, string];
  notVersioned?: boolean;
};

// prettier-ignore
const renderAsBackgroundImage = ({
  selector,
  backgroundImageName,
  directory,
  filetype,
  notVersioned = false,
}: renderAsBackgroundImageInterface): JSX.Element => {
  const versionSuffix = notVersioned ? '' : '@2x';
  return (
    <style global jsx>{`
      @media only screen and (min-width: 200px) {
        /* Small screen, non-retina */
        ${selector} {
          background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${directory}/${backgroundImageName}_mobile.${filetype[0]});
        }
      }

      @media only screen and (-webkit-min-device-pixel-ratio: 2) and (min-width: 200px),
        only screen and (min-resolution: 200dpi) and (min-width: 200px), {
        /* Small screen, retina, stuff to override above media query */
        ${selector} {
          background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${directory}/${backgroundImageName}_mobile${versionSuffix}.${filetype[0]});
        }
      }

      @media only screen and (min-width: ${TABLET_MIN}px) {
        /* Medium screen, non-retina */
        ${selector} {
          background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${directory}/${backgroundImageName}_tablet.${filetype[1]});
        }
      }

      @media only screen and (-webkit-min-device-pixel-ratio: 2) and (min-width: ${TABLET_MIN}px),
        only screen and (min-resolution: 200dpi) and (min-width: ${TABLET_MIN}px), {
        /* Medium screen, retina, stuff to override above media query */
        ${selector} {
          background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${directory}/${backgroundImageName}_tablet${versionSuffix}.${filetype[1]});
        }
      }

      @media only screen and (min-width: ${DESKTOP_MIN}px) {
        /* Large screen, non-retina */
        ${selector} {
          background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${directory}/${backgroundImageName}_desktop.${filetype[2]});
        }
      }

      @media only screen and (-webkit-min-device-pixel-ratio: 2) and (min-width: ${DESKTOP_MIN}px),
        only screen and (min-resolution: 200dpi) and (min-width: ${DESKTOP_MIN}px), {
        /* Large screen, retina, stuff to override above media query */
        ${selector} {
          background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${directory}/${backgroundImageName}_desktop${versionSuffix}.${filetype[2]});
        }
      }
    `}</style>
  );
};

export default renderAsBackgroundImage;
